import { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Minus, Plus, Trash2, Repeat } from "lucide-react";
import { CartItemWithSubscription } from "@/stores/cartStore";
import { triggerHaptic } from "@/lib/haptics";

interface SwipeableCartItemProps {
  item: CartItemWithSubscription;
  onRemove: (variantId: string) => void;
  onUpdateQuantity: (variantId: string, quantity: number) => void;
}

const SWIPE_THRESHOLD = 80;
const DELETE_THRESHOLD = 120;

export const SwipeableCartItem = ({ item, onRemove, onUpdateQuantity }: SwipeableCartItemProps) => {
  const [translateX, setTranslateX] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const startX = useRef(0);
  const currentX = useRef(0);
  const itemRef = useRef<HTMLDivElement>(null);

  const handleTouchStart = (e: React.TouchEvent) => {
    startX.current = e.touches[0].clientX;
    currentX.current = e.touches[0].clientX;
    setIsDragging(true);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging) return;
    
    currentX.current = e.touches[0].clientX;
    const diff = startX.current - currentX.current;
    
    if (diff > 0) {
      const resistance = diff > SWIPE_THRESHOLD ? 0.3 : 1;
      const newTranslate = Math.min(diff * resistance, DELETE_THRESHOLD + 20);
      setTranslateX(-newTranslate);
      
      if (diff >= SWIPE_THRESHOLD && translateX > -SWIPE_THRESHOLD) {
        triggerHaptic('light');
      }
      if (diff >= DELETE_THRESHOLD && translateX > -DELETE_THRESHOLD) {
        triggerHaptic('medium');
      }
    } else {
      setTranslateX(0);
    }
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
    
    const diff = startX.current - currentX.current;
    
    if (diff >= DELETE_THRESHOLD) {
      triggerHaptic('heavy');
      setTranslateX(-300);
      setTimeout(() => {
        onRemove(item.variantId);
      }, 200);
    } else {
      setTranslateX(0);
    }
  };

  const handleQuantityChange = (newQuantity: number) => {
    triggerHaptic('light');
    onUpdateQuantity(item.variantId, newQuantity);
  };

  const handleDelete = () => {
    triggerHaptic('medium');
    onRemove(item.variantId);
  };

  return (
    <div className="relative overflow-hidden rounded-lg">
      <div 
        className="absolute inset-y-0 right-0 flex items-center justify-end bg-destructive px-6"
        style={{ width: Math.abs(translateX) + 60 }}
      >
        <Trash2 className="h-6 w-6 text-white" />
      </div>
      
      <div
        ref={itemRef}
        className={`relative flex flex-col gap-2 rounded-lg border bg-background p-3 ${
          item.subscription?.enabled 
            ? 'border-primary/30 bg-primary/5' 
            : ''
        } ${isDragging ? '' : 'transition-transform duration-200 ease-out'}`}
        style={{ transform: `translateX(${translateX}px)` }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {/* Top row: image + product details */}
        <div className="flex items-start gap-3">
          <div className="h-14 w-14 flex-shrink-0 overflow-hidden rounded-md bg-secondary/20 sm:h-16 sm:w-16">
            {item.product.node.images?.edges?.[0]?.node ? (
              <img
                src={item.product.node.images.edges[0].node.url}
                alt={item.product.node.title}
                className="h-full w-full object-cover"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  const fallbackUrl = item.product.node.images?.edges?.[1]?.node?.url;
                  if (fallbackUrl && target.src !== fallbackUrl) {
                    target.src = fallbackUrl;
                  } else {
                    target.style.display = 'none';
                  }
                }}
              />
            ) : (
              <div className="flex h-full w-full items-center justify-center text-xs text-muted-foreground">No img</div>
            )}
          </div>
          
          <div className="min-w-0 flex-1 space-y-1">
            <h4 className="text-sm font-medium leading-tight whitespace-normal break-words">{item.product.node.title}</h4>
            {item.variantTitle !== "Default Title" && (
              <p className="text-xs leading-tight text-muted-foreground break-words">
                {item.selectedOptions.map(option => option.value).join(' • ')}
              </p>
            )}
            
            {item.subscription?.enabled && (
              <Badge 
                variant="secondary" 
                className="flex w-fit items-center gap-1 border-primary/20 bg-primary/10 text-[11px] text-primary whitespace-normal leading-tight"
              >
                <Repeat className="h-3 w-3" />
                Subscription · 3 months
              </Badge>
            )}
            
            {item.subscription?.enabled && (
              <p className="text-xs leading-tight text-primary break-words">
                {item.subscription.packType === "single" ? "bottle" : item.subscription.packType === "2-pack" ? "2-pack" : item.subscription.packType} monthly
              </p>
            )}
            
            <p className="font-semibold leading-none text-primary">
              ${parseFloat(item.price.amount).toFixed(2)}
              {item.subscription?.enabled && (
                <span className="ml-1 text-xs font-normal text-muted-foreground">/month</span>
              )}
            </p>
          </div>
        </div>

        {/* Bottom row: quantity controls + trash icon */}
        <div className="flex items-center justify-between border-t border-border/50 pt-2">
          <div className="flex items-center gap-1.5">
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8 touch-manipulation transition-transform active:scale-95 sm:h-7 sm:w-7"
              onClick={() => handleQuantityChange(item.quantity - 1)}
            >
              <Minus className="h-3.5 w-3.5 sm:h-3 sm:w-3" />
            </Button>
            <span className="w-7 text-center text-sm font-medium">{item.quantity}</span>
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8 touch-manipulation transition-transform active:scale-95 sm:h-7 sm:w-7"
              onClick={() => handleQuantityChange(item.quantity + 1)}
            >
              <Plus className="h-3.5 w-3.5 sm:h-3 sm:w-3" />
            </Button>
          </div>
          
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 touch-manipulation text-muted-foreground hover:text-destructive sm:h-7 sm:w-7"
            onClick={handleDelete}
          >
            <Trash2 className="h-4 w-4 sm:h-3.5 sm:w-3.5" />
          </Button>
        </div>
      </div>
    </div>
  );
};